import { twJoin } from 'tailwind-merge';

import {
  ReferenceChampionBadge,
  type ReferenceChampionExplainSource,
} from '@/components/directory/referenceChampionBadge';
import { ResponseTime } from '@/components/responseTime';
import { HStack } from '@/components/ui/hstack';
import { useReferenceChampionBadge } from '@/hooks/useReferenceChampionBadge';

type ExpertMetricsSize = 'text-xs' | 'text-sm';

type ExpertMetricsProps = {
  expertId: string | number;
  /** Raw response time, looked up against the tier buckets by `ResponseTime` itself. */
  responseTime?: string | null;
  /** Pre-resolved tier label, for callers that already have one. Wins over `responseTime`. */
  responseTimeLabel?: string;
  /** Type scale of the host: `text-xs` for directory cards and the picker, `text-sm` for profile. */
  size?: ExpertMetricsSize;
  /** Lets the badge be pressed for what earns it. Off by default — most hosts are press targets too. */
  explain?: boolean;
  /** Names this surface on the analytics that go with the explanation. Only read when `explain`. */
  explainSource?: ReferenceChampionExplainSource;
  className?: string;
  /** Applied to each metric, not the row. For hosts that lay metrics out as fixed-width columns. */
  metricClassName?: string;
  /** Catches the badge's mark with a single glint. Reserved for an expert's own profile. */
  celebrate?: boolean;
};

/**
 * Whether an expert has anything to show in the metrics row. Exported because the directory
 * card needs the answer before rendering, to pick between a real footer and a collapsed
 * spacer — keeping "what counts as a metric" defined once rather than duplicated in the card.
 */
export function useHasExpertMetrics({
  expertId,
  responseTime,
  responseTimeLabel,
}: Pick<ExpertMetricsProps, 'expertId' | 'responseTime' | 'responseTimeLabel'>): boolean {
  const badge = useReferenceChampionBadge(expertId);

  const hasResponseTime = Boolean(responseTimeLabel) || Boolean(responseTime);

  return hasResponseTime || badge !== null;
}

/**
 * The measured facts about an expert, as one row: response time, then the Reference Champion
 * badge. Replaces six hand-assembled copies of this row that had drifted on gap, type scale,
 * and — in three cases — silently omitted the badge entirely, because nothing tied showing one
 * metric to showing the other.
 *
 * Response time comes first: both metrics open with words rather than a mark, and the
 * measurement reads as the more ordinary fact, so the award landing second reads as the
 * addition.
 *
 * Deliberately not used everywhere a response time renders: the anonymous cards, anonymous
 * profile, and product-support card show one without an expert behind it, so there's no badge
 * to pair it with.
 */
export function ExpertMetrics({
  expertId,
  responseTime,
  responseTimeLabel,
  size = 'text-xs',
  explain = false,
  explainSource,
  className,
  metricClassName,
  celebrate = false,
}: ExpertMetricsProps) {
  // Wide gap: at 12px this read as one run-on line rather than two separate facts.
  return (
    <HStack
      data-component="expert-metrics"
      className={twJoin('flex-wrap items-center gap-x-5 gap-y-2', className ?? '')}>
      <ResponseTime
        responseTime={responseTime ?? undefined}
        responseTimeLabel={responseTimeLabel}
        fontSize={size}
        className={metricClassName}
      />

      <ReferenceChampionBadge
        expertId={expertId}
        className={metricClassName}
        fontSize={size}
        explainOnPress={explain}
        explainSource={explainSource}
        celebrate={celebrate}
      />
    </HStack>
  );
}
